import { useState, useCallback } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import { useTracking } from 'react-tracking';

import { useMount } from '@simplywallst/ui-core';

import { Sentry } from '@/components/Sentry';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';
import type { DateRange } from './hooks/useDateRangeState';

import {
  usePortfolioDetailedReturns,
  getPresetDateRange,
  useDateRangeState,
} from './hooks';

import { getISODateAtUTC } from './utils';

import { PopulatedState } from './components/PopulatedState';
import { LoadingState } from './components/LoadingState';
import { ErrorState } from './components/ErrorState';
import { DateSelector } from './components/DateSelector';
import { ReturnsCalculationButton } from './components/ReturnsCalculationButton';

import { useMockedPortfolioDetailedReturns } from './hooks/useMockedPortfolioDetailedReturns';
import { Button } from '@simplywallst/ui-core/next';
import ChevronDownSmallSolid from '@simplywallst/ui-core/icons/ChevronDownSmallSolid';

type Props = {
  portfolio: Portfolio;
};

type SubmittedDateRange =
  | DateRange
  | {
      start: null;
      end: null;
    };

type PresetDateOption = ComponentPropsWithoutRef<
  typeof DateSelector
>['selectedOption'];

export const ReturnsPanel = ({ portfolio }: Props) => {
  const { id, updatedAt, currencyISO } = portfolio;

  const [{ start, end }, setSubmittedDateRange] = useState<SubmittedDateRange>({
    start: null,
    end: null,
  });

  const { data, status } = usePortfolioDetailedReturns({
    id,
    startDate: start && getISODateAtUTC(start),
    endDate: end && getISODateAtUTC(end),
    updatedAt,
  });

  const {
    selectedOption,
    setSelectedOption,
    dateRange,
    onCustomDateRangeChange,
  } = useDateRangeState();

  const handleSelectedOptionChange = useCallback(
    (option: PresetDateOption) => {
      setSelectedOption(option);
      if (option !== 'custom') {
        const newDateRange = getPresetDateRange(option);
        setSubmittedDateRange(newDateRange);
      }
    },
    [setSelectedOption, setSubmittedDateRange]
  );

  const { trackEvent, Track } = useTracking({
    type: 'track',
    modifier: 'ReturnsPanel',
  });

  useMount(() => {
    trackEvent({
      action: 'landed',
    });
  });

  const startDate = data?.Portfolio.detailedReturns.startDate;

  return (
    <Sentry
      sentryCapture={{
        team: 'wombats',
      }}
      globalDisabled
    >
      <Track>
        <header className="grid w-full max-w-screen-xl grid-rows-[max-content_max-content] gap-x2 justify-self-center p-x2 md:grid-cols-[max-content_1fr] md:grid-rows-[auto] lg:mb-x2 lg:p-0 lg:px-x3">
          <DateSelector
            selectedOption={selectedOption}
            setSelectedOption={handleSelectedOptionChange}
            dateRange={dateRange}
            onChange={onCustomDateRangeChange}
            isDisabled={status === 'pending'}
            onSubmit={() => setSubmittedDateRange(dateRange)}
            inceptionStartDate={new Date(startDate ?? new Date())}
          />
          <div className="grid grid-cols-[max-content] justify-between gap-x4 md:justify-end">
            <ReturnsCalculationButton />
          </div>
        </header>
        {status === 'pending' ? (
          <LoadingState currencyISO={currencyISO} />
        ) : status === 'error' ? (
          <ErrorState currencyISO={currencyISO} />
        ) : typeof data === 'undefined' ? (
          <ErrorState currencyISO={currencyISO} /> // TODO: Update to empty state when BE return empty data instead of an error
        ) : (
          <PopulatedState portfolioDetailedReturns={data.Portfolio} />
        )}
      </Track>
    </Sentry>
  );
};

export const NonTransactionalReturnsPanel = ({ portfolio }: Props) => {
  const { currencyISO } = portfolio;

  const { data } = useMockedPortfolioDetailedReturns({
    portfolio,
  });

  const { trackEvent, Track } = useTracking({
    type: 'track',
    modifier: 'NonTransactionalReturnsPanel',
  });

  useMount(() => {
    trackEvent({
      action: 'landed',
    });
  });

  return (
    <Sentry
      sentryCapture={{
        team: 'wombats',
      }}
      globalDisabled
    >
      <Track>
        <header className="grid w-full max-w-screen-xl grid-rows-[max-content_max-content] gap-x2 justify-self-center p-x2 md:grid-cols-[max-content_1fr] md:grid-rows-[auto] lg:mb-x2 lg:p-0 lg:px-x3">
          <div className="flex items-center gap-x2">
            <Button variant="outline" isDisabled={true}>
              <span>Since inception</span>
              <ChevronDownSmallSolid />
            </Button>
            <span className="text-xs text-softer">Unknown date range</span>
          </div>
          <div className="grid grid-cols-[max-content] justify-between gap-x4 md:justify-end">
            <ReturnsCalculationButton />
          </div>
        </header>
        {typeof data === 'undefined' ? (
          <ErrorState currencyISO={currencyISO} />
        ) : (
          <PopulatedState portfolioDetailedReturns={data.Portfolio} />
        )}
      </Track>
    </Sentry>
  );
};
